import {Button, css, view, Icon} from '@sail/ui';
import {external} from '@sail/icons/react/Icon';
import {
  ButtonsMessage as ButtonsMessageType,
  Conversation,
  ConversationButton,
} from '@stripe-internal/conversations-client';
import {useCallback, useContext} from 'react';
import {animate, AnimatePresence} from '@sail/engine';
import {useAnalytics} from '../../../AnalyticsProvider';
import {useUniversalHelpDotComDesign} from '../../../FeatureFlagsProvider';
import {
  DotComChatBubbleAnimationContext,
  linkColors,
  useTheme,
} from '../../../index';
import {dotComColors} from '../../../theme/dotCom';

type ButtonProps = {
  conversation: Conversation;
  button: ConversationButton;
  disabled?: boolean;
  dotComAnimation?: boolean;
};

const SingleButton = ({
  conversation,
  button,
  disabled,
  dotComAnimation,
}: ButtonProps) => {
  const analytics = useAnalytics();
  const useDotComDesign = useUniversalHelpDotComDesign();
  const theme = useTheme();

  const onPress = useCallback(() => {
    analytics.action('presented_button_clicked', {
      buttonUrl: button.url,
      buttonTitle: button.title,
    });
    conversation.action({name: 'button-clicked', data: button});
  }, [analytics, button, conversation]);

  if (useDotComDesign) {
    return (
      <AnimatePresence>
        <view.a
          data-testid="button-link"
          href={button.url}
          target={button.url}
          onClick={onPress}
          css={{
            transformOrigin: 'right bottom',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            marginLeft: 'auto',
            fontWeight: 400,
            border: '1px solid',
            borderColor: dotComColors.brand300,
            borderRadius: 'medium',
            background: dotComColors.brand,
            paddingX: '14px',
            paddingY: 'small',
            whiteSpace: 'pre-line',
            overflowWrap: 'break-word',
            color: dotComColors.brand600,
            width: 'fit-content',
            textAlign: 'start',
            transition: '150ms linear',
            ':hover': {
              color: theme.linkColor,
              borderColor: '#c4ccd8',
              boxShadow:
                '0 0.25rem 0.75rem 0 rgba(0, 0, 0, 0.06), 0 0.125rem 0.25rem 0 rgba(0, 0, 0, 0.1)',
            },
          }}
          uses={[
            animate.in({
              duration: 250,
              delay: 500,
              opacity: 0,
              transform: 'translateX(20px)',
              easing: 'cubic-bezier(0.33, 1, 0.68, 1)',
              disabled: disabled || !dotComAnimation,
            }),
          ]}
        >
          <Icon icon={external} />
          {button.title}
        </view.a>
      </AnimatePresence>
    );
  }

  const isLinkAudience = theme.audience === 'link';
  const linkButtonStyles = css({
    backgroundColor: linkColors.neutral900,
    color: linkColors.neutral,
    boxShadow: 'none',
    ':hover:not(:active):not([aria-disabled="true"])': {
      backgroundColor: linkColors.neutral900,
      color: linkColors.neutral,
    },
  });

  return (
    /**
     * Using button.url as target instead of '_blank' to prevent opening multiple
     * tabs of the same button from the support widget. If the button has
     * already been clicked on from here, the already-opened tab will be focused.
     */
    <Button
      css={{width: 'fill'}}
      uses={[isLinkAudience && linkButtonStyles]}
      data-testid="button-link"
      href={button.url}
      target={button.url}
      onPress={onPress}
      disabled={disabled}
      type="secondary"
      size="medium"
    >
      {button.title}
    </Button>
  );
};

type Props = {
  conversation: Conversation;
  message: ButtonsMessageType;
  messageId?: string;
};

export const ButtonsMessage = ({conversation, message, messageId}: Props) => {
  const {animatedMessageId} = useContext(DotComChatBubbleAnimationContext);

  const buttons = message.buttons.map((button) => (
    <SingleButton
      key={button.url}
      conversation={conversation}
      button={button}
      dotComAnimation={messageId === animatedMessageId}
    />
  ));

  return (
    <view.div
      data-testid="buttons-message"
      css={{
        marginTop: 'small',
        marginBottom: 'small',
        stack: 'y',
        gap: 'small',
      }}
    >
      {buttons}
    </view.div>
  );
};
